/**
 * 业务页面中 配合宽表组件 PerformanceMetricsTable使用的逻辑
 */
import React, { useMemo, useState } from "react";
import { useIntl, getLocale, useSelector, shallowEqual } from "umi";
import moment from "moment";
import MetricAndUpDown from "@/pages/dashboard/components/MetricAndUpDown";

import {
  formatNum,
  formatPercentNum,
  getUpAndDownStatus,
  getWeekDay,
} from '@/pages/dashboard/utils/utils';
import { PagingQuery, SortBy } from "@/pages/dashboard/pages/overview/components/OverviewTable/types";
import { Bubble } from "tea-component";
import SvgIcon from "@/pages/home/components/SvgIcon";
import { POE_DEMO_GAME_CODE_LIST } from "@/pages/dashboard/_config/constant";
import { getExplainVal } from "@/pages/dashboard/utils/getExplainVal";
import { formatFunnelFilterValues } from "../../_config/utils";

export function useTableState() {
  // 加载状态
  const [loading, setLoading] = useState<boolean>(true);
  const [isError, setIsError] = useState<boolean>(false);
  // 分页
  const [paging, setPaging] = useState<PagingQuery>({ pageSize: 10, pageIndex: 1 });
  // 排序
  const [sort, setSort] = useState<SortBy[]>([]);
  // 总条数
  const [total, setTotal] = useState(0);

  // 表格数据
  const [tableData, setTableData] = useState([]);

  // 聚合数据
  const [statisticsData, setStatisticsData] = useState([]);

  return {
    loading, setLoading,
    isError, setIsError,
    paging, setPaging,
    sort, setSort,
    total, setTotal,
    tableData, setTableData,
    statisticsData, setStatisticsData,
  };
}

// 表格上传参数
export function getTableRequestParams(props: any) {
  const { gameCode, formValue, stepValue, accumulated, dimensionOptions, segValue, selectType, sort, paging, hasAddInput, haveDate, api, ...resetParams } = props || {};
  const { rangeTime, ...filters } = formValue?.filterValue || {};
  const { by, order } = sort?.[0] || { by: 'date', order: 'desc' };
  const dimension = dimensionOptions?.map((item: any) => item?.value);
  const newBy = !dimension?.includes(by) ? `${by?.replace(selectType, '')}_${segValue}` : by;

  const accumulation: any = {
    by_zone: accumulated?.includes('zone'),
    by_region: accumulated?.includes('region'),
    by_country: accumulated?.includes('country'),
    by_channel: accumulated?.includes('channel'),
    by_system: accumulated?.includes('system'),
    by_language: accumulated?.includes('language'),
    by_app_version: accumulated?.includes('app_version'),
    by_network_type: accumulated?.includes('network_type'),
    by_memory: accumulated?.includes('memory'),
    by_game_stage: accumulated?.includes('game_stage'),
    by_character_class: accumulated?.includes('character_class'),
    by_user_type: accumulated?.includes('user_type'),
    by_stage: true,
    by_date: haveDate,
  };
  if (hasAddInput) accumulation.by_store_channel = accumulated?.includes('store_channel');
  if (filters?.ua_network) accumulation.by_ua_network = accumulated?.includes('ua_network');
  if (filters?.campaign) accumulation.by_campaign = accumulated?.includes('campaign');
  if (filters?.ab_test) accumulation.by_ab_test = accumulated?.includes('ab_test');
  let params = {
    game_code: gameCode,
    // 筛选器上传参数
    start_time: rangeTime?.[0],
    end_time: rangeTime?.[1],
    filters: { ...formatFunnelFilterValues(filters), 'stage_list': stepValue },
    // 维度筛选器上传参数
    accumulation,
    // 分页
    page: paging.pageIndex,
    page_size: paging.pageSize,
    // 排序
    // sorted_by: order ? by : '',
    // desc: !!(order && order === 'desc')
    sorted_by: newBy,
    desc: order === 'desc',
  };

  // 新模式无需formatFilterValues
  if (api) {
    filters.stage = stepValue;
    params.filters = filters;
  }

  params = {
    ...params,
    ...resetParams
  };

  return params;
}

export function useTableColumns() {
  const { formatMessage } = useIntl();
  const gameCode = useSelector((state: any) => state?.dashboard?.currentGame?.game_code, shallowEqual);
  const isPoeDemo = POE_DEMO_GAME_CODE_LIST?.includes(gameCode);
  const isRegisterFunnel = window?.location.href?.includes('funnel/registerFunnel');
  const isPoeFunnel = isPoeDemo && isRegisterFunnel;

  // 日期列, 周六日的颜色高亮,
  const DATE_COLUMN = {
    key: 'date',
    header: formatMessage({ id: "common_date" }),
    width: 160,
    fixed: "left",
    align: "left",
    render(record: any) {
      /** Sum提示需要用到useState，
      *所以拿出来单独处理，
      *不然render里拿不到最新的的state
      */
      return <DateCell record={record} formatMessage={formatMessage} />;
    }
  };

  // 日期列, 周六日的颜色高亮 & Sum行增加提示
  function DateCell({ record, formatMessage }: any) {
    const [isIconHover, setIsIconHover] = useState(false);
    const locale = getLocale();

    const reg = /\((.+?)\)/gi;
    const weekValue = record?.date?.match(reg)?.[0]?.replace(/^\(/, "")?.slice(0, -1);
    let weekStr = '';
    if (weekValue) {
      if (weekValue?.includes('Null') || weekValue?.includes('去空')) {
        weekStr = `(${weekValue})`;
      } else {
        weekStr = weekValue;
      }
    }
    const dateVal = record?.date ? record?.date?.split('(') : ['-'];
    const isSunOrSat = weekStr === 'Sun' || weekStr === 'Sat' || weekStr === '周日' || weekStr === '周六';
    const showSumTip = record.date === 'Sum' || record.date === '汇总值';

    return (
      <div style={{ display: 'flex', alignItems: 'center', lineHeight: 1 }}>
        <span className='table-text'>{dateVal?.[0]}</span>
        <span className={isSunOrSat ? 'text-week-b' : 'text-week'}>
          {weekStr}
          {showSumTip && (
            <div className='port-item'>
              <div onMouseOver={() => { setIsIconHover(true) }} onMouseOut={() => { setIsIconHover(false) }} >
                <Bubble
                  dark
                  placement="top-start"
                  arrowPointAtCenter
                  style={{ maxWidth: locale === 'en-US' ? '235px' : '253px' }}
                  content={formatMessage({ id: 'overview_sum_tip' })}
                >
                  <div><SvgIcon className='port-icon' type={isIconHover ? 'alert-circle-action' : 'alert-circle-m'} /></div>
                </Bubble>
              </div>
            </div>
          )}
        </span>
      </div>
    );
  }

  // Accumulate 列
  const getAccumulateColumns = (accumulates: string[] = [], haveDate?: boolean, customColumnFn?: (value: string) => string) => {
    const columns: any = [];
    const poeFunnelObj: any = {
      channel: formatMessage({ id: 'funnel_channel_poe' }),
      language: formatMessage({ id: 'funnel_channel_language_poe' }),
      app_version: formatMessage({ id: 'funnel_app_version_poe' }),
      network_type: formatMessage({ id: 'funnel_channel_network_poe' }),
      memory: formatMessage({ id: 'funnel_channel_memory_poe' }),
    };
    accumulates.forEach((value: string, i) => {
      let headerName = formatMessage({ id: `common_${value}` });
      if (isPoeFunnel) {
        headerName = poeFunnelObj?.[value] || formatMessage({ id: `common_${value}` });
      }

      // @ts-ignore
      if (i > 0 || !haveDate) {
        let column;
        if (typeof customColumnFn === 'function') {
          column = customColumnFn(value);
        } else {
          column = {
            key: value,
            header: headerName,
            copyKey: `${value}_name`,
            align: 'left',
            width: 200,
            render(record) {
              const name = `${value}_name`;
              return record?.[name] || "-";
            }
          };
        }
        return columns.push(column);
      }
    });

    return columns;
  };

  // 指标列
  // 通常情况下metricsKey就是metricsInfo对象的key, 但是国家对比页面的metricsKey是受控的
  const getMetricColumns = (metricsKey, metricsInfo, explainInfo, selectType, segValue = 'conversion', firstStepId = '') => {
    const isShowUpDown = selectType !== "value_only";
    return metricsKey.reduce((columns, key) => {
      const info = metricsInfo[key];
      // @ts-ignore
      columns.push(
        {
          key: key + selectType,
          copyConfig: {
            hideCopyDod: key === firstStepId,
            hideCopySdlw: key === firstStepId,
          },
          copyKey: `${key}_${segValue}`,
          header: info?.name,
          align: "right",
          explain: getExplainVal(explainInfo, info?.rainbowKey, getLocale()),
          width: isShowUpDown ? info?.width : info?.onlyValueWidth,
          isPercent: info?.isPercent,
          render(record) {
            const metric = record?.metrics?.[`${key}_${segValue}`] || {};
            const { progressWidth, metricValue, } = metric;

            return (
              <>
                <div className={"table-text-bg"} style={{ width: `${progressWidth * 0.95}%` }} />
                {/* <div className={'table-text-bg'} style={{width: `${progressWidth}%`}} /> */}
                <MetricAndUpDown
                  metric={metricValue}
                  updown={isShowUpDown && key !== firstStepId ? { data: metric[`${selectType}PercentStr`], status: metric[`${selectType}Status`] } : null}
                  hasTooltip={false}
                />
              </>);
          }
        },
      );
      return columns;
    }, []);
  };

  return {
    DATE_COLUMN,
    getAccumulateColumns,
    getMetricColumns,
  };
}

// 表格返回数据格式整理
export function useTableDataFormat(tableData, metricsInfo, timeType = "Day", segValue = 'conversion') {
  const { formatMessage } = useIntl();
  return useMemo(() => {
    if (!Array.isArray(tableData)) return [];
    const WEEK_DAY_ARR = [
      formatMessage({ id: 'common_mon' }),
      formatMessage({ id: 'common_tues' }),
      formatMessage({ id: 'common_wed' }),
      formatMessage({ id: 'common_thur' }),
      formatMessage({ id: 'common_fri' }),
      formatMessage({ id: 'common_sat' }),
      formatMessage({ id: 'common_sun' }),
    ];
    return tableData.map((data) => {
      const { date, metrics, type, ...accumulates } = data || {};
      // 日期列数据 (如果type有值， 说明是聚合数据)
      // const dateStr = timeType === "Month" ? moment(date).format("YYYY-MM") : moment(date).format("YYYY-MM-DD") + getWeekDay(date, WEEK_DAY_ARR);
      let dateStr = getDateFormatString(timeType, date); // 要显示周一日期
      dateStr = timeType === "Month" ? dateStr : dateStr + getWeekDay(dateStr, WEEK_DAY_ARR);

      // 指标列数据
      Object.keys(metricsInfo).forEach(key => {
        const newKey = `${key}_${segValue}`;
        const { value, dod, sdlw } = metrics[newKey] || { value: "", dod: "", sdlw: "" };
        const { isPercent, decimals, isUserType, dodDecimals } = metricsInfo[key] || {};
        const progressWidth = isPercent && value >= 0 ? value * 100 : 0; // 单元格内进度条的宽度
        const metricValue = isPercent ? formatPercentNum(value, decimals, false) : formatNum(parseFloat(value), false, isUserType);
        const symbol = value >= 0 ? '' : '-';
        const symbolMetricValue = symbol + metricValue;

        const dodStatus = getUpAndDownStatus(dod, false);
        const sdlwStatus = getUpAndDownStatus(sdlw, false);
        const dodPercentStr = formatPercentNum(dod, dodDecimals, true);
        const sdlwPercentStr = formatPercentNum(sdlw, dodDecimals, true);

        metrics[newKey] = {
          progressWidth: progressWidth > 100 ? 100 : progressWidth,
          metricValue: symbolMetricValue,
          dodStatus,
          sdlwStatus,
          dodPercentStr,
          sdlwPercentStr,
          ...metrics[newKey]
        };
      });

      return {
        date: dateStr,
        // 周六或者周日 表格行高亮
        isSatOrSun: timeType === "Day" && (moment(date).format('E') === '6' || moment(date).format('E') === '7'),
        metrics,
        ...accumulates
      };
    });
  }, [tableData, getLocale(), segValue]);
}
// 表格返回统计值数据格式整理
export function useStatisticsDataFormat(statisticsData, metricsInfo, showStatistic, segValue = 'conversion', fisrtRowName = 'date') {
  const { formatMessage } = useIntl();

  const statisticsObj = useMemo(() => {
    if (!Array.isArray(statisticsData)) return {};
    return statisticsData.reduce((acc, data) => {
      const { date, metrics, type, ...accumulates } = data || {};
      // 日期列数据 (如果type有值， 说明是聚合数据)
      const dateStr = formatMessage({ id: `common_${type}` }); // 统计值行的数据, 日期列要展示对应的聚合标题
      // 指标列数据
      Object.keys(metricsInfo).forEach(key => {
        const newKey = `${key}_${segValue}`;
        const { value } = metrics[newKey] || { value: "", dod: "" };
        const { isPercent, decimals, isUserType } = metricsInfo[key] || {};
        const progressWidth = isPercent && value >= 0 ? value * 100 : 0; // 单元格内进度条的宽度
        const metricValue = isPercent ? formatPercentNum(value, decimals, false) : formatNum(parseFloat(value), false, isUserType);
        const symbol = value >= 0 ? '' : '-';
        const symbolMetricValue = symbol + metricValue;

        metrics[newKey] = {
          progressWidth: progressWidth > 100 ? 100 : progressWidth,
          metricValue: symbolMetricValue,
          dodStatus: "no",
          sdlwStatus: "no",
          dodPercentStr: "",
          sdlwPercentStr: "",
          ...metrics[newKey]
        };
      });

      acc[type] = {
        // 周六或者周日 表格行高亮
        isSatOrSun: false,
        metrics,
        ...accumulates,
        [fisrtRowName]: dateStr,
      };
      return acc;
    }, {});
  }, [statisticsData, getLocale(), segValue]);
  return Object.keys(statisticsObj).length > 0 ? showStatistic.map(type => statisticsObj[type]) : [];
}

/**
 * 处理在 Day, Week , Month不同维度下, 显示的日期格式
 * @param dateDimension 日期维度
 * @param date 日期
 * @return 返回处理后符合产品要求的日期格式字符串
 */
export const getDateFormatString = (dateDimension, date: string) => {
  switch (dateDimension) {
    case "Week": {
      const weekOfDay = moment(date).format('E');
      return moment(date).subtract(Number(weekOfDay) - 1, 'days').format("YYYY-MM-DD");
    }
    case "Month": {
      return moment(date).format("YYYY-MM");
    }
    case "Day":
    default:
      return moment(date).format("YYYY-MM-DD");
  }
};
